import { datadogRum } from '@datadog/browser-rum';

import { getConfig } from 'src/utils/config';

export const getErrorMessage = (error: any): string | null => {
  if (Array.isArray(error) && error.length > 0) {
    return error.map(getErrorMessage).join(' | ');
  }
  if (typeof error === 'object') {
    return error?.message || JSON.stringify(error);
  }
  if (typeof error === 'string' && error.length > 0) {
    return error;
  }
  if (typeof error === 'number') {
    return error.toString();
  }

  return null;
};

export const log = {
  error: (message: string, meta?: any) => {
    datadogLogger.error(message, meta);
    if (getConfig('environment') !== 'production') {
      // eslint-disable-next-line no-console
      console.error(message, meta);
    }
  },
  info: (...message: unknown[]) => {
    datadogLogger.info(...message);
    if (getConfig('environment') !== 'production') {
      // eslint-disable-next-line no-console
      console.info(...message);
    }
  },
  warn: (...message: unknown[]) => {
    datadogLogger.warn(...message);
    if (getConfig('environment') !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(...message);
    }
  },
  trace: (...message: unknown[]) => {
    // For debugging/tracing events that we don't want to go to Datadog
    if (getConfig('environment') !== 'production') {
      // eslint-disable-next-line no-console
      console.debug(...message);
    }
  },
};

interface DataDogLogContext extends Record<string, unknown> {
  message?: string;
  activity?: string;
}

const mapLogInput: (
  message?: unknown,
  context?: unknown,
) => { message: string; context?: DataDogLogContext } = (message, context) => ({
  message: (message as string | undefined) ?? 'log',
  context: context as DataDogLogContext,
});

const datadogLogger = {
  info: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);

    datadogRum.addAction(context?.activity ?? message, {
      ...context,
      messageInput,
      level: 'info',
    });
  },
  error: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);
    const error = context?.error ?? new Error(message);
    datadogRum.addError(error, {
      ...context,
      message,
      level: 'error',
    });
  },
  warn: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);

    datadogRum.addAction(context?.activity ?? message, {
      ...context,
      message,
      level: 'warn',
    });
  },
};
